/* src/components/customGame/setup/panels/FeedbackPanel.js */
import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '../../ui/Accordion';

// Re-using the SettingsSection helper
const SettingsSection = ({ title, children, helpText = "", className = "" }) => (
    <div className={`mb-6 p-4 border border-gray-200 rounded-md shadow-sm bg-white ${className}`}>
        <h3 className="text-lg font-medium text-gray-800 mb-3">{title}</h3>
        {helpText && <p className="text-sm text-gray-500 mb-3 -mt-2">{helpText}</p>}
        <div className="space-y-4">
            {children}
        </div>
    </div>
);

SettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
    className: PropTypes.string,
};

const FEEDBACK_MODALITIES = [
    { key: 'Visual', label: 'Visual (e.g., border flash, color change)' },
    { key: 'Audio', label: 'Audio (e.g., beep, buzz)' },
    { key: 'Textual', label: 'Textual (e.g., "Correct", "Incorrect" - if UI space allows)' },
];

const VISUAL_FEEDBACK_ELEMENTS = [
    { key: 'Stimulus Border', label: 'Stimulus Area Border Flash' },
    { key: 'Fixation Color', label: 'Fixation Point Color Change' },
    { key: 'Background Flash', label: 'Brief Background Flash' },
];

const SOUND_OPTIONS = [ // Simplified list for now
    { key: 'Default Beep', label: 'Default Correct Beep' },
    { key: 'Default Buzz', label: 'Default Incorrect Buzz' },
    { key: 'Warning Tone', label: 'Default Warning/Miss Tone' },
    { key: 'Short Click', label: 'Short Click' },
    { key: 'Soft Chime', label: 'Soft Chime' },
    { key: 'Muted Error', label: 'Muted Error Sound' },
    { key: 'None', label: 'None (No Sound)' },
];


const FeedbackPanel = ({ feedbackConfig, uiInGameConfig, audioInGameConfig, onConfigChange }) => {
    const currentFeedback = feedbackConfig || {};
    const currentUiInGame = uiInGameConfig || {};
    const currentAudioInGame = audioInGameConfig || {};

    // Generic handler for top-level sections (Feedback, UI.InGame, Audio.InGame)
    const handleSectionChange = (sectionPath, key, value) => {
        // Ensure numeric values related to duration or delay are parsed
        let processedValue = value;
        if (typeof value === 'string' && (key.toLowerCase().includes('durationms') || key.toLowerCase().includes('delayms'))) {
            processedValue = parseInt(value, 10);
        } else if (typeof value === 'string' && key.toLowerCase().includes('volume')) {
            processedValue = parseFloat(value);
        }
        onConfigChange(`${sectionPath}.${key}`, processedValue);
    };
    
    // Specific handler for multi-select checkbox array like Feedback.Correctness.Modality
    const handleMultiSelectChange = (sectionPath, key, optionKey, isChecked) => {
        const currentSelection = currentFeedback[sectionPath.split('.').pop()]?.[key] || []; // e.g. currentFeedback.Correctness.Modality
        let newSelection;
        if (isChecked) {
            newSelection = [...currentSelection, optionKey];
        } else {
            newSelection = currentSelection.filter(item => item !== optionKey);
        }
        onConfigChange(`${sectionPath}.${key}`, newSelection);
    };

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-full">

            <SettingsSection title="Immediate Performance Feedback" helpText="Configure feedback given right after a user's response (or lack thereof).">
                {/* --- Correctness Feedback --- */}
                <Accordion title="Correctness Feedback (for Hits & False Alarms)" id="correctness-feedback-accordion" defaultOpen={true}>
                    <div className="space-y-3 p-3 bg-gray-50 rounded-b-md border border-t-0 border-gray-200">
                        <div className="flex items-center">
                            <input
                                id="correctnessEnabled"
                                type="checkbox"
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                checked={currentFeedback.Correctness?.Enabled !== undefined ? currentFeedback.Correctness.Enabled : true}
                                onChange={(e) => handleSectionChange('Feedback.Correctness', 'Enabled', e.target.checked)}
                            />
                            <label htmlFor="correctnessEnabled" className="ml-2 block text-sm font-medium text-gray-700">
                                Enable Correctness Feedback
                            </label>
                        </div>
                        {currentFeedback.Correctness?.Enabled && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Feedback Modalities:</label>
                                    {FEEDBACK_MODALITIES.map(modality => (
                                        <div key={modality.key} className="flex items-center ml-2 my-1">
                                            <input
                                                id={`correctnessModality-${modality.key}`}
                                                type="checkbox"
                                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                checked={(currentFeedback.Correctness?.Modality || []).includes(modality.key)}
                                                onChange={(e) => handleMultiSelectChange('Feedback.Correctness', 'Modality', modality.key, e.target.checked)}
                                            />
                                            <label htmlFor={`correctnessModality-${modality.key}`} className="ml-2 block text-sm text-gray-600">{modality.label}</label>
                                        </div>
                                    ))}
                                </div>
                                {(currentFeedback.Correctness?.Modality || []).includes('Visual') && (
                                    <div>
                                        <label htmlFor="correctnessVisualElement" className="block text-sm font-medium text-gray-700 mb-1">Visual Feedback Element:</label>
                                        <select
                                            id="correctnessVisualElement"
                                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                            value={currentFeedback.Correctness?.VisualElement || 'Stimulus Border'}
                                            onChange={(e) => handleSectionChange('Feedback.Correctness', 'VisualElement', e.target.value)}
                                        >
                                            {VISUAL_FEEDBACK_ELEMENTS.map(el => <option key={el.key} value={el.key}>{el.label}</option>)}
                                        </select>
                                    </div>
                                )}
                                <div>
                                    <label htmlFor="correctnessDurationMs" className="block text-sm font-medium text-gray-700 mb-1">Feedback Duration (ms):</label>
                                    <input type="number" id="correctnessDurationMs" value={currentFeedback.Correctness?.DurationMs !== undefined ? currentFeedback.Correctness.DurationMs : 200} onChange={(e) => handleSectionChange('Feedback.Correctness', 'DurationMs', e.target.value)} min="50" max="1000" step="50" className="mt-1 input-class w-full" />
                                </div>
                            </>
                        )}
                    </div>
                </Accordion>

                {/* --- Missed Match Feedback --- */}
                <Accordion title="Missed Match Feedback (for Omissions/Misses)" id="missed-match-feedback-accordion">
                     <div className="space-y-3 p-3 bg-gray-50 rounded-b-md border border-t-0 border-gray-200">
                        <div className="flex items-center">
                            <input id="missedMatchEnabled" type="checkbox" checked={currentFeedback.MissedMatch?.Enabled !== undefined ? currentFeedback.MissedMatch.Enabled : true} onChange={(e) => handleSectionChange('Feedback.MissedMatch', 'Enabled', e.target.checked)} className="h-4 w-4 input-class" />
                            <label htmlFor="missedMatchEnabled" className="ml-2 block text-sm font-medium text-gray-700">Enable Missed Match Feedback</label>
                        </div>
                        {currentFeedback.MissedMatch?.Enabled && (
                            <>
                                {/* Similar Modality, VisualElement, DurationMs inputs as Correctness */}
                                {/* TODO: Add these inputs, mirroring Correctness structure */}
                                <p className="text-xs text-gray-500 p-2 bg-gray-100 rounded">Modality, Visual Element, and Duration settings for Missed Matches will be similar to Correctness Feedback. (To be implemented)</p>
                                <div>
                                    <label htmlFor="missedMatchDelayMs" className="block text-sm font-medium text-gray-700 mb-1">Feedback Delay (ms after response window closes):</label>
                                    <input type="number" id="missedMatchDelayMs" value={currentFeedback.MissedMatch?.DelayMs !== undefined ? currentFeedback.MissedMatch.DelayMs : 500} onChange={(e) => handleSectionChange('Feedback.MissedMatch', 'DelayMs', e.target.value)} min="0" max="2000" step="100" className="mt-1 input-class w-full" />
                                </div>
                            </>
                        )}
                    </div>
                </Accordion>
                
                {/* --- Acknowledgment Feedback (mutually exclusive with Correctness) --- */}
                 <Accordion title="Input Acknowledgment Feedback (Non-Evaluative)" id="ack-feedback-accordion">
                     <div className="space-y-3 p-3 bg-gray-50 rounded-b-md border border-t-0 border-gray-200">
                        <div className="flex items-center">
                            <input id="ackEnabled" type="checkbox" disabled={currentFeedback.Correctness?.Enabled} checked={currentFeedback.Acknowledgment?.Enabled && !currentFeedback.Correctness?.Enabled} onChange={(e) => handleSectionChange('Feedback.Acknowledgment', 'Enabled', e.target.checked)} className="h-4 w-4 input-class" />
                            <label htmlFor="ackEnabled" className="ml-2 block text-sm font-medium text-gray-700">Enable Input Acknowledgment (if Correctness Feedback is OFF)</label>
                        </div>
                        {currentFeedback.Acknowledgment?.Enabled && !currentFeedback.Correctness?.Enabled && (
                            <>
                                {/* TODO: Add Modality, VisualElement, DurationMs for Acknowledgment */}
                                <p className="text-xs text-gray-500 p-2 bg-gray-100 rounded">Modality, Visual Element, and Duration settings for Acknowledgment. (To be implemented)</p>
                            </>
                        )}
                         {currentFeedback.Correctness?.Enabled && <p className="text-xs text-gray-500 mt-1">Acknowledgment feedback is disabled when Correctness Feedback is active.</p>}
                    </div>
                </Accordion>
            </SettingsSection>

            <SettingsSection title="In-Game Display Elements" helpText="Control what information is visible during gameplay.">
                <div className="flex items-center">
                    <input id="showTrialCounter" type="checkbox" checked={currentUiInGame.ShowTrialCounter !== undefined ? currentUiInGame.ShowTrialCounter : true} onChange={(e) => handleSectionChange('UI.InGame', 'ShowTrialCounter', e.target.checked)} className="h-4 w-4 input-class"/>
                    <label htmlFor="showTrialCounter" className="ml-2 block text-sm font-medium text-gray-700">Show Trial Counter (e.g., "Trial 5/60")</label>
                </div>
                <div className="flex items-center">
                    <input id="showLiveScore" type="checkbox" checked={currentUiInGame.ShowLiveScore !== undefined ? currentUiInGame.ShowLiveScore : false} onChange={(e) => handleSectionChange('UI.InGame', 'ShowLiveScore', e.target.checked)} className="h-4 w-4 input-class"/>
                    <label htmlFor="showLiveScore" className="ml-2 block text-sm font-medium text-gray-700">Show Live Score/Accuracy</label>
                </div>
                {/* UI.InGame.StimulusRenderStyle could go here if it's a simple dropdown */}
            </SettingsSection>

            <SettingsSection title="In-Game Audio Settings" helpText="Adjust volumes and sounds for feedback during the game.">
                <div>
                    <label htmlFor="feedbackVolume" className="block text-sm font-medium text-gray-700 mb-1">Auditory Feedback Volume (relative to master, 0.0 to 1.0)</label>
                    <input type="number" id="feedbackVolume" value={currentAudioInGame.FeedbackVolume !== undefined ? currentAudioInGame.FeedbackVolume : 0.7} onChange={(e) => handleSectionChange('Audio.InGame', 'FeedbackVolume', e.target.value)} min="0" max="1" step="0.05" className="mt-1 input-class w-full"/>
                </div>
                <div>
                    <label htmlFor="correctSound" className="block text-sm font-medium text-gray-700 mb-1">Correct Response Sound</label>
                    <select id="correctSound" value={currentAudioInGame.CorrectFeedbackSound || 'Default Beep'} onChange={(e) => handleSectionChange('Audio.InGame', 'CorrectFeedbackSound', e.target.value)} className="mt-1 select-class w-full">
                        {SOUND_OPTIONS.map(opt => <option key={opt.key} value={opt.key}>{opt.label}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="incorrectSound" className="block text-sm font-medium text-gray-700 mb-1">Incorrect Response Sound</label>
                    <select id="incorrectSound" value={currentAudioInGame.IncorrectFeedbackSound || 'Default Buzz'} onChange={(e) => handleSectionChange('Audio.InGame', 'IncorrectFeedbackSound', e.target.value)} className="mt-1 select-class w-full">
                        {SOUND_OPTIONS.map(opt => <option key={opt.key} value={opt.key}>{opt.label}</option>)}
                    </select>
                </div>
                 <div>
                    <label htmlFor="missedMatchSound" className="block text-sm font-medium text-gray-700 mb-1">Missed Match Sound</label>
                    <select id="missedMatchSound" value={currentAudioInGame.MissedMatchFeedbackSound || 'Warning Tone'} onChange={(e) => handleSectionChange('Audio.InGame', 'MissedMatchFeedbackSound', e.target.value)} className="mt-1 select-class w-full">
                        {SOUND_OPTIONS.map(opt => <option key={opt.key} value={opt.key}>{opt.label}</option>)}
                    </select>
                </div>
            </SettingsSection>
            
            {/* Helper class names for inputs/selects if not globally defined */}
            <style jsx global>{`
                .input-class {
                    /* Common input styling */
                    padding: 0.5rem 0.75rem;
                    border: 1px solid #D1D5DB; /* gray-300 */
                    border-radius: 0.375rem; /* rounded-md */
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); /* shadow-sm */
                }
                .input-class:focus {
                    outline: none;
                    border-color: #4F46E5; /* indigo-500 */
                    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.3); /* ring-indigo-500 with opacity */
                }
                .select-class { /* For select elements */
                    padding-left: 0.75rem; padding-right: 2.5rem; padding-top: 0.5rem; padding-bottom: 0.5rem;
                    border: 1px solid #D1D5DB; border-radius: 0.375rem;
                }
                .select-class:focus {
                    outline: none; border-color: #4F46E5;
                    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.3);
                }
            `}</style>

        </div>
    );
};

FeedbackPanel.propTypes = {
    feedbackConfig: PropTypes.object.isRequired,
    uiInGameConfig: PropTypes.object.isRequired,
    audioInGameConfig: PropTypes.object.isRequired,
    onConfigChange: PropTypes.func.isRequired,
    // pathPrefix is not explicitly needed here if handleSectionChange uses full paths
};

export default FeedbackPanel;